import { useState, useRef } from 'react';
import { Loader2, Pencil, Pin, PinOff } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/cn';
import type { ChatRuntime } from '@/shared/chat-runtime';
import { useUpdatePin, useUpdateTitle } from '@/entities/thread';

type ThreadListProps = {
  agentId: string;
  selectedThreadId: string | null;
  runtime: ChatRuntime;
  onSelect: (threadId: string | null) => void;
};

export function ThreadList({
  selectedThreadId,
  runtime,
  onSelect,
}: ThreadListProps) {
  const query = runtime.threadQueries.useThreadsQuery();
  const { updateTitle } = useUpdateTitle();
  const { updatePin } = useUpdatePin();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const isCommittingRef = useRef(false);

  async function commitRename(threadId: string) {
    if (isCommittingRef.current) return;
    isCommittingRef.current = true;
    const trimmed = editDraft.trim();
    setEditingId(null);
    try {
      if (trimmed && trimmed !== threads.find((t) => t.id === threadId)?.title) {
        await updateTitle({ threadId, title: trimmed });
      }
    } finally {
      isCommittingRef.current = false;
    }
  }

  async function togglePin(threadId: string, pinnedAt: string | null) {
    await updatePin({ threadId, pinned: pinnedAt === null });
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 p-4" role="status">
        <Loader2 className="size-4 animate-spin" aria-label="Loading threads" />
        <span>Loading threads…</span>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <span className="text-sm text-muted-foreground">We could not load your conversation history.</span>
        <Button variant="outline" size="sm" className="w-fit" onClick={() => query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const threads = query.data ?? [];

  if (threads.length === 0) {
    return (
      <div className="p-4">
        <span>No previous conversations.</span>
      </div>
    );
  }

  return (
    <div role="listbox" aria-label="Conversation history" className="h-full overflow-y-auto">
      {threads.map((thread) => {
        const isSelected = thread.id === selectedThreadId;
        return (
          <div
            key={thread.id}
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted',
              isSelected && 'bg-accent',
            )}
            onClick={() => { if (editingId === null) onSelect(thread.id); }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && editingId === null) {
                e.preventDefault();
                onSelect(thread.id);
              }
            }}
          >
            {editingId === thread.id ? (
              <input
                autoFocus
                aria-label="Rename conversation"
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { void commitRename(thread.id); }
                  if (e.key === 'Escape') { setEditingId(null); }
                }}
                onBlur={() => { void commitRename(thread.id); }}
                onClick={(e) => e.stopPropagation()}
                className="w-full rounded border border-input bg-transparent px-1 py-0.5 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            ) : (
              <>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm">{thread.title ?? thread.id}</span>
                  {thread.updatedAt !== null && (
                    <span className="truncate text-xs text-muted-foreground">{thread.updatedAt}</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={thread.pinnedAt ? 'Unpin conversation' : 'Pin conversation'}
                  title={thread.pinnedAt ? 'Unpin conversation' : 'Pin conversation'}
                  className={cn(thread.pinnedAt && 'text-primary')}
                  onClick={(e) => {
                    e.stopPropagation();
                    void togglePin(thread.id, thread.pinnedAt);
                  }}
                >
                  {thread.pinnedAt ? <PinOff /> : <Pin />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Rename conversation"
                  onClick={(e) => {
                    // Prevent the row's onClick (select) from firing
                    e.stopPropagation();
                    setEditingId(thread.id);
                    setEditDraft(thread.title ?? thread.id);
                  }}
                >
                  <Pencil />
                </Button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
